import React from 'react';
import { motion } from 'framer-motion';
import { Badge } from './ui/badge';
import { achievements } from '../data/portfolio';
import { Award, GraduationCap, Star, Shield, TrendingUp, Layers } from 'lucide-react';

import ParticleField from './ParticleField';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' }
  })
};

const iconMap = {
  award: Award,
  'graduation-cap': GraduationCap,
  star: Star,
  shield: Shield,
  'trending-up': TrendingUp,
  layers: Layers
};

const colorMap = {
  primary: { bg: 'hsl(var(--primary) / 0.1)', border: 'hsl(var(--primary) / 0.25)', text: 'hsl(var(--primary))' },
  secondary: { bg: 'hsl(var(--secondary) / 0.1)', border: 'hsl(var(--secondary) / 0.25)', text: 'hsl(var(--secondary))' },
  accent: { bg: 'hsl(var(--accent) / 0.1)', border: 'hsl(var(--accent) / 0.25)', text: 'hsl(var(--accent))' }
};

const AchievementsSection = () => {
  return (
    <section id="achievements" className="relative py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
      <div
        className="absolute inset-0 bg-grid opacity-20 pointer-events-none"
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 30% 50%, hsl(var(--primary) / 0.05) 0%, transparent 60%)' }}
      />
       {/* Grid dots */}
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />

      {/* Particles */}
      <div className="absolute inset-0">
        <ParticleField />
      </div>  
      <div className="relative z-10 max-w-7xl mx-auto">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16 "
        >
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 mb-4 font-mono text-xs uppercase tracking-wider">
            Recognition
          </Badge>
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-foreground leading-tight">
            Achievements &
            <span className="gradient-text"> Milestones</span>
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {achievements.map((item, i) => {
            const Icon = iconMap[item.icon];
            const c = colorMap[item.color];

            return (
              <motion.div
                key={item.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                custom={i}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
                transition={{ duration: 0.3 }}
                className="glass glass-hover rounded-2xl p-6 flex flex-col gap-4"
              >
                <div className="flex items-start justify-between">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center"
                    style={{ background: c.bg, border: `1px solid ${c.border}` }}
                  >
                    <Icon size={22} style={{ color: c.text }} />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">{item.year}</span>
                </div>

                <div>
                  <h3 className="font-heading font-bold text-foreground text-base leading-snug mb-1">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-xs font-mono" style={{ color: c.text }}>
                    {item.issuer}
                  </p>
                </div>

                <p className="text-muted-foreground text-sm leading-relaxed mt-auto">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AchievementsSection;
